'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { CanLogoIcon } from '@/../public/assets/icons';
import { signupStyles } from '../../signup-templates/signup.styles';
import { Box, Stack, Text, Button, Field, Alert } from '@chakra-ui/react';
import { PasswordInput } from '@/components/ui/password-input';
import useCountdownTimer from '@/hooks/shared/useCountDown';
import { resendOtp, verifyEmailWithOtp, verifyWithLink } from '@/services/auth/auth.service';
import { removeCookie, setCookies } from '@/app/actions/auth/cookies.action';
import { TVerifyEmail, verifyEmailSchema } from './verify-email.validator';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import BarLoader from '@/components/ui/loaders/BarLoader';
import useAuthFunc from '@/hooks/auth/useAuthFunc';
import { toaster } from '@/components/ui';

const EmailVerification = ({ registeredEmail, token }: { registeredEmail: string; token?: string }) => {
   const { handleLogin } = useAuthFunc();
   const [isVerifying, setIsVerifying] = useState<boolean>(token ? true : false);
   const { timeLeft, resetTimer } = useCountdownTimer({ time: 2 * 60 });

   const verifyLink = useCallback(
      async (token: string) => {
         const [error, data] = await verifyWithLink(token);

         setIsVerifying(false);

         if (error) {
            console.log(error);
            return;
         }

         await removeCookie('registered-email');

         await handleLogin(data.response);
      },
      [handleLogin],
   );

   useEffect(() => {
      if (token) verifyLink(token);
   }, [token, verifyLink]);

   const onResendOtp = async () => {
      const [error] = await resendOtp(registeredEmail);

      if (error) {
         console.log(error);
         toaster.error({ title: error.response?.data.message as string });
         return;
      }

      resetTimer(2 * 60);
      toaster.success({ title: 'Otp resent successfully', description: 'Please check your email' });
      await setCookies('registered-email', registeredEmail, new Date(Date.now() + 5 * 60 * 1000));
   };

   const {
      register,
      handleSubmit,
      reset,
      formState: { errors },
      setError,
   } = useForm<TVerifyEmail>({
      mode: 'onChange',
      resolver: zodResolver(verifyEmailSchema),
   });

   const onSubmit = handleSubmit(async (value: TVerifyEmail) => {
      setIsVerifying(true);
      const [error, data] = await verifyEmailWithOtp({ ...value, email: registeredEmail });
      if (error) {
         setError('root', { message: error.response?.data.message as string, type: 'manual' });
         setIsVerifying(false);
         console.log(error);
         return;
      }

      await removeCookie('registered-email');
      await handleLogin(data.response);
      setIsVerifying(false);
   });
   return (
      <>
         {isVerifying && <BarLoader loadingText="Verifying..." />}
         <Box minH="72px">
            <CanLogoIcon />
         </Box>
         <Text
            as="h2"
            color="white"
            fontSize={{ base: '4xl', sm: '5xl', md: '12xl' }}
            fontWeight="bold"
            textAlign="center"
         >
            EMAIL VERIFICATION
         </Text>
         <Text color="white" textStyle="md" textAlign="center" mt="-10px" mb="2">
            Please enter your verification 6 digit code on this mail{'\n'}
            <Text as="span" fontWeight="bold">
               {registeredEmail}
            </Text>
         </Text>

         <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
            <Stack gap="4" align="flex-start" minW="full">
               {errors.root?.message && (
                  <Alert.Root status="error">
                     <Alert.Indicator />
                     <Alert.Title>{errors.root.message}</Alert.Title>
                  </Alert.Root>
               )}
               <Field.Root invalid={Boolean(errors.otp?.message)}>
                  <Field.Label textStyle="xs" color="white" mb="1">
                     Enter Code
                     <Box as="span" color="error">
                        *
                     </Box>
                  </Field.Label>
                  <Box mb="2" w="full">
                     <PasswordInput
                        placeholder="Enter Code"
                        size="md"
                        h="10"
                        rounded="12px"
                        bg="#202020"
                        {...register('otp')}
                     />
                     <Field.ErrorText>{errors.otp?.message}</Field.ErrorText>
                  </Box>
               </Field.Root>
               <Box css={signupStyles.send_code} w="full" mt="-3" mb="2">
                  <Box>
                     <Text color="white" fontSize="sm" fontWeight="bold">
                        {timeLeft}
                     </Text>
                  </Box>
                  <Box>
                     <button
                        onClick={onResendOtp}
                        style={{
                           color: 'white',
                           fontSize: 'sm',
                           fontWeight: 'bold',
                           cursor: 'pointer',
                           textDecoration: 'underline',
                        }}
                        type="button"
                     >
                        Resend Code
                     </button>
                  </Box>
               </Box>
               <Box css={signupStyles.signup_btn_con}>
                  <Button
                     type="reset"
                     onClick={() => reset()}
                     bg="darkerBtn"
                     textStyle="xs"
                     color="white"
                     h="10"
                     maxW="36"
                     minW="36"
                     p="0"
                  >
                     Reset
                  </Button>
                  <Button type="submit" textStyle="xs" h="10" maxW="36" minW="36" p="0" disabled={isVerifying}>
                     Verify
                  </Button>
               </Box>
            </Stack>
         </Box>
      </>
   );
};

export default EmailVerification;
